import { default  as AgentComponent } from '../components/AIAgentComp/Agent';
import back4app2 from '../back4app2';
import { 
  Agent, 
  AgentCharLimitExceededError, 
  AgentStatus, 
  AgentStatusError, 
  AgentsSubscriptionChatMessagesHardLimitError, 
  AuthenticationError, 
  AuthorizationError, 
  Back4app2Error,
  BlockedResourceError, 
  ChatMessage, 
  ChatMessageStatus, 
  NetworkError, 
  NotFoundError, 
  PaymentPendingError, 
  Subscription,
  Task,
  TaskStatus,
} from '@back4app2/sdk';
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { BACK4APP_DOT_COM_SITE_URL } from '../settings';
import toast from 'react-hot-toast';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from 'react';
import LoadingMessage from '../components/LoadingMessage';
import ErrorMessage from '../components/ErrorMessage';
import { Message } from '../types/Agent';
import { pushGTMEvent } from '../utils/gtm';
import { extractLatestAppInfoFromMessages } from '../utils/agentResultParser';
import AgentUpgradeModal from '../components/AIAgentComp/AgentUpgradeModal';
import { useNavigate } from 'react-router-dom';

const AGENT_PLAN_FEATURES = [
  'Higher agent message limits',
  'Faster response priority',
  'Includes hosting & custom domains',
  'Priority support',
];

const AGENT_PLAN_PRICE = process.env.REACT_APP_AGENT_PLAN_PRICE_LABEL || '$29';

interface MyAgentState {
  isLoading: boolean;
  loadingErrorMessage?: string;
  messages?: ChatMessage[];
  paymentErrorMessage?: string;
}

const INITIAL_STATE: MyAgentState = {
  isLoading: true,
  loadingErrorMessage: undefined,
  messages: undefined,
  paymentErrorMessage: undefined
};

enum MyAgentActionType {
  RESET,
  FINISH_LOADING,
  FINISH_SENDING,
  SET_PAYMENT_ERROR_MESSAGE
}

const reset = () => ({
  type: MyAgentActionType.RESET
} as const);

const finishLoading = (errorMessage?: string, messages?: ChatMessage[]) => ({
  type: MyAgentActionType.FINISH_LOADING,
  payload: {
    errorMessage,
    messages,
  }
} as const);

const finishSending = (message: ChatMessage) => ({
  type: MyAgentActionType.FINISH_SENDING,
  payload: {
    message
  }
} as const);

const setPaymentErrorMessage = (errorMessage?: string) => ({
  type: MyAgentActionType.SET_PAYMENT_ERROR_MESSAGE,
  payload: {
    errorMessage
  }
} as const);

type MyAgentAction = ReturnType<typeof reset> | ReturnType<typeof finishLoading> | ReturnType<typeof finishSending> | ReturnType<typeof setPaymentErrorMessage>;

const reducer = (state: MyAgentState = INITIAL_STATE, action: MyAgentAction): MyAgentState => {
  switch (action.type) {
    case MyAgentActionType.RESET:
      return {
        ...INITIAL_STATE
      };
    
    case MyAgentActionType.FINISH_LOADING:
      return {
        ...state,
        isLoading: false,
        loadingErrorMessage: action.payload.errorMessage,
        messages: (action.payload.messages && [...action.payload.messages]) || undefined
      }
    
    case MyAgentActionType.FINISH_SENDING:
      const messages = [...(state.messages || [])];

      if (!messages.find(message => message.id === action.payload.message.id)) {
        messages.push(action.payload.message);
      }

      return {
        ...state,
        messages
      }

    case MyAgentActionType.SET_PAYMENT_ERROR_MESSAGE:
      return {
        ...state,
        paymentErrorMessage: action.payload.errorMessage
      }
  }
};

const MyAgent = forwardRef<
  { setInputContent: (content: string) => void },
  {
    agentId: string;
    agents: Agent[] | undefined;
    isSidebarOpen: boolean;
    onPreviewUrlChange?: (url?: string) => void;
    onAgentTurnStarted?: () => void; // notify when user sends a message
    onAgentTurnEnded?: () => void; // notify when assistant finishes a response
    onUpgradePlan?: (priceId: string, frameId?: string) => void;
    /** Callback to open upgrade modal at parent level (full screen) */
    onUpgrade?: () => void;
  }
>(({ agentId, agents, isSidebarOpen, onPreviewUrlChange, onAgentTurnStarted, onAgentTurnEnded, onUpgradePlan, onUpgrade }, ref) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const agentRef = useRef<{ setInputContent: (content: string) => void }>(null);

  const { isLoading, loadingErrorMessage, messages, paymentErrorMessage } = state;
  const [isResponding, setIsResponding] = useState(false);
  const [respondingMessageId, setRespondingMessageId] = useState<string | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const navigate = useNavigate();

  const agent = useMemo(() => agents && agents.find(agent => agent.id === agentId), [agentId, agents]);

  const chatId = useMemo(() => agent && agent.mainChat && agent.mainChat.id, [agent]);

  const redirectedRef = useRef(false);
  const redirectOnce = (to: string) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    window.location.replace(to);
  };

  useImperativeHandle(ref, () => ({
    setInputContent: (content: string) => {
      if (agentRef.current) {
        agentRef.current.setInputContent(content);
      }
    },
    // Expose send function for mobile input
    sendMessage: async (content: string): Promise<boolean> => {
      return onSend(content);
    },
    // Expose stop function for mobile input
    stopGeneration: async (): Promise<boolean> => {
      return onStop();
    },
    // Expose busy state for mobile input
    get isBusy() {
      return isResponding;
    }
  }));

  useEffect(
    () => {
      dispatch(reset());
      
      let subscription: Subscription;

      if (chatId) {
        subscription = back4app2.subscribeToChatMessages(
          chatId,
          (error, snapshot) => {  
            if (error) {
              if (error instanceof NetworkError) {
                console.error('network error', error);
                dispatch(finishLoading('Network error when loading chat messages. Check your internet connection and try again.'));
              } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
                window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
              } else if(error instanceof NotFoundError) {
                dispatch(finishLoading('Chat not found. Reload page, create a new agent or contact our supporting team.'));
                return;
              } else {
                console.error('unexpected error loading chat messages', error);
                dispatch(finishLoading('Unexpected error when loading chat messages. Please try again.'));
              }
            } else {
              dispatch(finishLoading(undefined, snapshot));
            }
          }
        );
      }
            
      return () => {
        if (subscription) {
          subscription.unsubscribe();
          dispatch(reset());
        }
      };
    },
    [chatId]
  );

  useEffect(() => {
    if (paymentErrorMessage) {
      setIsUpgradeModalOpen(true);
    }
  }, [paymentErrorMessage]);

  const [processingPlanId, setProcessingPlanId] = useState<string | undefined>(undefined);

  const handleUpgradeClose = useCallback(() => {
    setIsUpgradeModalOpen(false);
    setProcessingPlanId(undefined);
    dispatch(setPaymentErrorMessage(undefined));
  }, [dispatch]);

  const handleUpgradeNow = useCallback((priceId: string) => {
    // Paddle is now handled inside AgentUpgradeModal; keep compatibility flag
    setProcessingPlanId(undefined);
  }, []);

  const translatedMessages = useMemo(
    () => (messages && messages.reduce(
      (messages, message): Message[] => {
        return [
          ...messages,
          {
            id: `${message.id}-user`,
            role: 'user',
            content: message.question
          },
          {
            id: `${message.id}-assistant`,
            role: 'assistant',
            content: message.aiResponse || '',
            isTyping: !message.respondedAt,
            hasFailed: message.status === ChatMessageStatus.FAILED,
            error: message.error
          }
        ];
      },
      [] as Message[]
    )) || undefined,
    [messages]
  )

  const assistantIsTyping = useMemo(
    () =>
      translatedMessages?.some(
        (message) => message.role === 'assistant' && message.isTyping && !message.hasFailed
      ) || false,
    [translatedMessages]
  );

  // Sync busy state on initial load (handles page refresh while agent is working)
  // This ensures AppPanel stays in loading state after page refresh if agent is still working
  const hasNotifiedInitialBusy = useRef(false);
  useEffect(() => {
    // If assistant is typing when messages first load, notify parent
    if (!hasNotifiedInitialBusy.current && assistantIsTyping && onAgentTurnStarted) {
      hasNotifiedInitialBusy.current = true;
      onAgentTurnStarted();
    }
  }, [assistantIsTyping, onAgentTurnStarted]);

  // Reset flag when assistant stops typing so it can trigger again on next refresh
  useEffect(() => {
    if (!assistantIsTyping) {
      hasNotifiedInitialBusy.current = false;
    }
  }, [assistantIsTyping]);

  useEffect(() => {
    if (assistantIsTyping) {
      setIsResponding(true);
    } else {
      setIsResponding(false);
    }
  }, [assistantIsTyping]);

  // Extract app info and notify parent
  useEffect(() => {
    if (onPreviewUrlChange && translatedMessages) {
      const appInfo = extractLatestAppInfoFromMessages(translatedMessages);
      onPreviewUrlChange(appInfo.url);
    }
  }, [translatedMessages, onPreviewUrlChange]);

  // Notify parent when assistant finishes a response (once per message)
  const lastHandledAssistantId = useRef<string | null>(null);
  useEffect(() => {
    if (!translatedMessages || !onAgentTurnEnded) return;
    const last = [...translatedMessages].reverse().find((m) => m.role === 'assistant');
    if (!last) return;

    // Treat FAILED messages as "turn ended" even if respondedAt was never set (isTyping may stay true)
    const turnEnded = !last.isTyping || !!last.hasFailed;

    if (turnEnded && last.id !== lastHandledAssistantId.current) {
      lastHandledAssistantId.current = last.id;
      setIsResponding(false);
      setRespondingMessageId(null); // Clear the responding message ID
      onAgentTurnEnded();
    }
  }, [translatedMessages, onAgentTurnEnded]);

  const onSend = async (content: string): Promise<boolean> => {
    if (isResponding) return false; // prevent multiple sends while busy
    // Mark start of agent work (for Preview loading)
    if (onAgentTurnStarted) onAgentTurnStarted();
    setIsResponding(true);
    let message;
    pushGTMEvent('submit_ai_prompt');

    try {
      message = await back4app2.askQuestionToAgent(agentId, content);
    } catch (e) {
      let errorMessage;
      if (e instanceof NetworkError) {
        console.error('network error', e);
        errorMessage = 'Network error when asking question to agent. Check your internet connection.';
      } else if (
        e instanceof AuthenticationError || 
        e instanceof AuthorizationError ||
        (e instanceof Back4app2Error &&
          (e.code === 'AUTHENTICATION_ERROR' || e.code === 'AUTHORIZATION_ERROR'))
      ) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return false;
      } else if (e instanceof AgentStatusError) {
        if (agent && agent.status === AgentStatus.FAILED) {
          errorMessage = 'Sorry, this agent has failed and can not respond to messages. Please create a new agent or contact our supporting team.';
        } else {
          errorMessage = 'This agent is still initializing and can not respond to messages. Please try again later.';
        }
      } else if (e instanceof BlockedResourceError) {
        errorMessage = 'This agent is blocked and can not respond to messages. If you believe that it is an error, please contact our supporting team.';
      } else if (e instanceof PaymentPendingError) {
        errorMessage = 'Please complete the payment process in order to ask questions to the agent.';
        dispatch(setPaymentErrorMessage(errorMessage))
      } else if (e instanceof AgentsSubscriptionChatMessagesHardLimitError) {
        errorMessage = 'Please subscribe to a paying plan in order to continue asking questions to the agent.';
        dispatch(setPaymentErrorMessage(errorMessage))
      } else if (e instanceof AgentCharLimitExceededError) {
        errorMessage = 'Content is too long.';
      } else if (e instanceof NotFoundError || (e as any)?.code === 'NOT_FOUND_ERROR') {
        toast.error(
          'Agent not found. Try again, create a new agent or contact our supporting team.',
          {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-lg text-sm",
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 6000
          }
        );
        setIsResponding(false);
        redirectOnce('/agents');
        return false;
      } else {
        console.error('unexpected error asking question to agent', e);
        errorMessage = 'Unexpected error when asking question to agent. Please try again later.';
      }

      toast.error(errorMessage as string, {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-lg text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
      // Ensure the busy state is cleared on error
      setIsResponding(false);
      if (onAgentTurnEnded) onAgentTurnEnded();
      return false;
    }

    dispatch(finishSending(message));
    // Track the message ID for stop functionality
    setRespondingMessageId(message.id);
    // isResponding will be cleared when assistant finishes (effect above)
    return true;
  }

  const onStop = async (): Promise<boolean> => {
    if (!respondingMessageId) return false;
    
    try {
      const task = await back4app2.stopAgentResponse(respondingMessageId);
      
      // If task is null, fast path succeeded immediately - no need to subscribe
      if (!task) {
        return true;
      }
      
      // Slow path - subscribe to task to catch worker failures
      const subscription = back4app2.subscribeToTask(
        task.id,
        (error: Back4app2Error | undefined, updatedTask: Task | undefined) => {
          if (error) {
            toast.error(error.message || 'Failed to stop generation', {
              className: 'bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm',
              icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
              duration: 6000,
            });
            subscription.unsubscribe();
          } else if (updatedTask?.status === TaskStatus.DONE || updatedTask?.status === TaskStatus.FAILED) {
            subscription.unsubscribe();
          }
        }
      );
      
      return true;
    } catch (e) {
      console.error('Error stopping agent response:', e);
      // Re-throw all Back4app2 errors so UI can display appropriate messages
      if (e instanceof Back4app2Error) {
        throw e;
      }
      throw new Back4app2Error('Failed to stop agent response', 'STOP_AGENT_ERROR');
    }
  }

  if (!chatId) {
    return (
      <div className="h-full flex flex-col">
        <LoadingMessage message="Loading agent..." />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <LoadingMessage message="Loading messages..." />
      </div>
    );
  }

  if (loadingErrorMessage) {
    return (
      <div className="h-full flex flex-col">
        <ErrorMessage message={loadingErrorMessage} />
      </div>
    );
  }

  return (
    <>
      <AgentComponent
        onSend={onSend}
        onStop={onStop}
        isBusy={isResponding}
        messages={translatedMessages}
        isSidebarOpen={isSidebarOpen}
        paymentErrorMessage={paymentErrorMessage}
        agentId={agentId}
        ref={agentRef}
        onUpgrade={onUpgrade || (() => setIsUpgradeModalOpen(true))}
      />
      <AgentUpgradeModal
        appId={agentId}
        isOpen={isUpgradeModalOpen}
        onClose={handleUpgradeClose}
      />
    </>
  );
});

export default MyAgent;


